import { Spinner } from "@chakra-ui/react";
import { chainDefiSectionMapping } from "@constants/common";
import usePageData from "@hooks/usePageData";
import useSanction from "@hooks/useSanction";
import { InputDropdown, Typography } from "@kelp-dao/kernel-design-system";
import { useSearchParams } from "next/navigation";
import {
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  DEFI_FILTERS_CHANGED,
  DEFI_OPPORTUNITIES_SORTED,
} from "@constants/analytics";
import { trackEvent } from "utils";
import { DeFiRow, PageData } from "./type";
import DefiListV2, { DefiListHeader } from "./DefiItem";
import FeaturedDefi from "./FeaturedDefi";

interface DropdownOption {
  id: string;
  label?: string;
  iconSrc?: string;
  rightElement?: ReactNode;
}

const Defi = () => {
  const pageData = usePageData() as PageData | undefined;
  const searchParams = useSearchParams();

  const defaultChainFilter = searchParams.get("chain");
  const defaultAssetFilter = searchParams.get("asset");
  const { isSanctioned } = useSanction();

  const ActivityData = useMemo(() => {
    const activity = new Set<string>();
    pageData?.pools.data.forEach((row) => {
      if (row.Activity != "") activity.add(row.Activity);
    });
    return Array.from(activity);
  }, [pageData?.pools.data]);

  const AssetData = useMemo(() => {
    const assets = new Set<string>();
    pageData?.pools.data.forEach((row) => {
      if (row.Activity != "") assets.add(row["Token 1 Icon"]);
    });
    return Array.from(assets);
  }, [pageData?.pools.data]);

  const sections = useMemo(() => {
    const sections: { [key: string]: DeFiRow[] } = {};
    pageData?.pools.data.forEach((row) => {
      if (row.Section) {
        sections[row.Section] = sections[row.Section] || [];
        sections[row.Section].push(row);
      }
    });
    return sections;
  }, [pageData?.pools.data]);

  const [selectedTag, setSelectedTag] = useState<DropdownOption[]>([]);

  const [selectedActivity, setSelectedActivity] = useState<DropdownOption[]>(
    []
  );

  const [selectedAsset, setSelectedAsset] = useState<DropdownOption[]>([]);

  // Sorting state for each section
  const [sortConfigs, setSortConfigs] = useState<{
    [key: string]: { key: any; direction: any };
  }>({});

  const handleSort = (section: string, key: string) => {
    const currentConfig = sortConfigs[section] || {
      key: "APY",
      direction: "descending",
    };
    let direction: any = "ascending";

    if (currentConfig.key === key && currentConfig.direction === "ascending") {
      direction = "descending";
    } else if (
      currentConfig.key === key &&
      currentConfig.direction === "descending"
    ) {
      setSortConfigs((prev) => ({
        ...prev,
        [section]: { key: null, direction: null },
      }));
      return;
    }

    setSortConfigs((prev) => ({ ...prev, [section]: { key, direction } }));

    // Track sorting event
    trackEvent(DEFI_OPPORTUNITIES_SORTED, {
      sort_by: key,
      sort_order: direction,
    });
  };

  const tags = useMemo((): DropdownOption[] => {
    return [
      { id: "All", label: "All" },
      ...Object.keys(sections).map((section) => ({
        id: section,
        label: section,
      })),
    ];
  }, [sections]);

  const activities = useMemo((): DropdownOption[] => {
    return [
      { id: "All", label: "All" },
      ...Object.values(ActivityData).map((activity) => ({
        id: activity,
        label: activity,
      })),
    ];
  }, [ActivityData]);

  const assets = useMemo((): DropdownOption[] => {
    return [
      { id: "All", label: "All" },
      ...Object.values(AssetData).map((asset) => ({ id: asset, label: asset })),
    ];
  }, [AssetData]);

  useEffect(() => {
    if (tags.length > 0) {
      setSelectedTag(tags);
    }
  }, [tags]);

  useEffect(() => {
    if (activities.length > 0) {
      setSelectedActivity(activities);
    }
  }, [activities]);

  useEffect(() => {
    if (assets.length > 0) {
      setSelectedAsset(assets);
    }
  }, [assets]);

  useEffect(() => {
    if (
      defaultChainFilter &&
      chainDefiSectionMapping[Number(defaultChainFilter)]
    ) {
      const targetSection = chainDefiSectionMapping[Number(defaultChainFilter)];
      const targetOption = tags.find((tag) => tag.id === targetSection);
      if (targetOption) {
        setSelectedTag([targetOption]);
      }
    }
  }, [defaultChainFilter, tags]);

  useEffect(() => {
    if (
      defaultAssetFilter &&
      Object.values(AssetData).includes(defaultAssetFilter)
    ) {
      const targetAsset = assets.find(
        (asset) => asset.id === defaultAssetFilter
      );
      if (targetAsset) {
        setSelectedAsset([targetAsset]);
      }
    }
  }, [defaultAssetFilter, assets]);

  const filteredSections = useMemo(() => {
    if (selectedTag.some((tag) => tag.id === "All")) {
      return sections;
    }

    let filtered = {};
    selectedTag.forEach((tag: DropdownOption) => {
      if (sections[tag.id]) {
        filtered = { ...filtered, [tag.id]: sections[tag.id] };
      }
    });

    return filtered;
  }, [sections, selectedTag]);

  const handleChainSelect = (options: DropdownOption[]) => {
    let updatedOptions = [...options];
    const hasAll = options.some((tag) => tag.id === "All");
    const hadAll = selectedTag.some((tag) => tag.id === "All");

    const allJustSelected = hasAll && !hadAll;
    const allJustDeselected = !hasAll && hadAll;

    if (allJustSelected) {
      updatedOptions = tags;
    } else if (allJustDeselected) {
      updatedOptions = [];
    } else if (!hasAll && !hadAll) {
      const nonAllOptions = options.filter((tag) => tag.id !== "All");
      const allNonAllSelected = nonAllOptions.length === tags.length - 1; // -1 for "All" option

      if (allNonAllSelected) {
        const allOption = tags.find((tag) => tag.id === "All");
        if (allOption) {
          updatedOptions = [allOption, ...nonAllOptions];
        }
      }
    } else if (hasAll && hadAll) {
      const nonAllOptions = options.filter((tag) => tag.id !== "All");
      if (nonAllOptions.length < tags.length - 1) {
        updatedOptions = nonAllOptions;
      }
    }

    setSelectedTag(updatedOptions);
    const chains = updatedOptions.map((tag) => tag.id).join(",");
    const activitiesSelected = selectedActivity.map((a) => a.id).join(",");
    const assetsSelected = selectedAsset.map((a) => a.id).join(",");
    trackEvent(DEFI_FILTERS_CHANGED, {
      changed_filter: "chain",
      chains,
      activities: activitiesSelected,
      assets: assetsSelected,
    });
  };

  const handleActivitySelect = (options: DropdownOption[]) => {
    let updatedOptions = [...options];
    const hasAll = options.some((activity) => activity.id === "All");
    const hadAll = selectedActivity.some((activity) => activity.id === "All");

    const allJustSelected = hasAll && !hadAll;
    const allJustDeselected = !hasAll && hadAll;

    if (allJustSelected) {
      updatedOptions = activities;
    } else if (allJustDeselected) {
      updatedOptions = [];
    } else if (!hasAll && !hadAll) {
      const nonAllOptions = options.filter((activity) => activity.id !== "All");
      const allNonAllSelected = nonAllOptions.length === activities.length - 1; // -1 for "All" option

      if (allNonAllSelected) {
        const allOption = activities.find((activity) => activity.id === "All");
        if (allOption) {
          updatedOptions = [allOption, ...nonAllOptions];
        }
      }
    } else if (hasAll && hadAll) {
      const nonAllOptions = options.filter((activity) => activity.id !== "All");
      if (nonAllOptions.length < activities.length - 1) {
        updatedOptions = nonAllOptions;
      }
    }

    setSelectedActivity(updatedOptions);
    const chains = selectedTag.map((tag) => tag.id).join(",");
    const activitiesSelected = updatedOptions.map((a) => a.id).join(",");
    const assetsSelected = selectedAsset.map((a) => a.id).join(",");
    trackEvent(DEFI_FILTERS_CHANGED, {
      changed_filter: "activity",
      chains,
      activities: activitiesSelected,
      assets: assetsSelected,
    });
  };

  const handleAssetSelect = (options: DropdownOption[]) => {
    let updatedOptions = [...options];
    const hasAll = options.some((asset) => asset.id === "All");
    const hadAll = selectedAsset.some((asset) => asset.id === "All");

    const allJustSelected = hasAll && !hadAll;
    const allJustDeselected = !hasAll && hadAll;

    if (allJustSelected) {
      updatedOptions = assets;
    } else if (allJustDeselected) {
      updatedOptions = [];
    } else if (!hasAll && !hadAll) {
      const nonAllOptions = options.filter((asset) => asset.id !== "All");
      const allNonAllSelected = nonAllOptions.length === assets.length - 1; // -1 for "All" option

      if (allNonAllSelected) {
        const allOption = assets.find((asset) => asset.id === "All");
        if (allOption) {
          updatedOptions = [allOption, ...nonAllOptions];
        }
      }
    } else if (hasAll && hadAll) {
      const nonAllOptions = options.filter((asset) => asset.id !== "All");
      if (nonAllOptions.length < assets.length - 1) {
        updatedOptions = nonAllOptions;
      }
    }

    setSelectedAsset(updatedOptions);
    const chains = selectedTag.map((tag) => tag.id).join(",");
    const activitiesSelected = selectedActivity.map((a) => a.id).join(",");
    const assetsSelected = updatedOptions.map((a) => a.id).join(",");
    trackEvent(DEFI_FILTERS_CHANGED, {
      changed_filter: "asset",
      chains,
      activities: activitiesSelected,
      assets: assetsSelected,
    });
  };

  const getFilteredRows = useCallback(
    (rows: DeFiRow[], featured = false) => {
      return rows.filter((row) => {
        const activityMatch = selectedActivity.some(
          (activity) => activity.id === "All" || activity.id === row.Activity
        );
        const assetMatch = selectedAsset.some(
          (asset) => asset.id === "All" || asset.id === row["Token 1 Icon"]
        );

        if (!featured) {
          return activityMatch && assetMatch;
        } else {
          return (
            activityMatch && assetMatch && row.Featured.toLowerCase() === "yes"
          );
        }
      });
    },
    [selectedActivity, selectedAsset]
  );

  const featuredDefi = useMemo(() => {
    if (selectedActivity.length > 0) {
      const filtered: any[] = [];
      Object.keys(filteredSections).map((section) =>
        filtered.push(...getFilteredRows(sections[section], true))
      );
      return filtered;
    } else {
      return [];
    }
  }, [filteredSections, getFilteredRows, sections, selectedActivity.length]);

  const renderMissingSelectionMessage = () => {
    let message = "";
    const sectionLength = Object.keys(filteredSections).length;
    const hasActivitySelection =
      selectedActivity.length > 0 &&
      !selectedActivity.every((activity) => activity.id === "All");
    const hasAssetSelection =
      selectedAsset.length > 0 &&
      !selectedAsset.every((asset) => asset.id === "All");

    if (!hasActivitySelection && sectionLength === 0) {
      message =
        "Please select atleast 1 Chain, Activity & Asset to see DeFi activities";
    } else if (!hasActivitySelection && sectionLength !== 0) {
      message = "Please select atleast 1 Activity to see DeFi activities";
    } else if (hasActivitySelection && sectionLength === 0) {
      message = "Please select atleast 1 Chain to see DeFi activities";
    } else if (
      hasActivitySelection &&
      sectionLength !== 0 &&
      !hasAssetSelection
    ) {
      message = "Please select atleast 1 Asset to see DeFi activities";
    } else {
      return;
    }
    return (
      <div className="flex justify-center items-center h-full md:h-[50vh] mt-4 md:mt-0">
        {message}
      </div>
    );
  };

  const [loadingTable, setLoadingTable] = useState(true);
  useEffect(() => {
    setLoadingTable(false);
  }, []);

  const customCheckboxStyle = {
    accentColor: "#075a5a",
    borderColor: "#075a5a",
  };

  return (
    <>
      <div
        className={`w-full flex justify-center max-w-full md:max-w-[440px] lg:max-w-[764px] xl:max-w-[930px] mx-auto ${
          isSanctioned
            ? "pointer-events-none opacity-50"
            : "pointer-events-auto opacity-100"
        }`}
      >
        <div className="rounded-3xl flex flex-col gap-3 w-full xl:mt-0">
          <div className="flex flex-col justify-between gap-4">
            <div className="flex flex-col gap-1">
              <Typography
                as="h1"
                type="heading"
                variant="default"
                className="text-text-primary text-xl font-medium md:font-semibold font-sans"
              >
                DeFi
              </Typography>
              <Typography
                as="span"
                type="body"
                variant="xsmall"
                className="text-text-secondary"
              >
                Use rsETH and explore rewards using DeFi opportunities
              </Typography>
            </div>
            <div className="flex gap-2 w-full flex-nowrap">
              <InputDropdown
                options={tags}
                selectedOptions={selectedTag}
                onOptionsSelect={handleChainSelect}
                multiSelect={true}
                placeholder="Networks"
                checkboxClassName="mr-2"
                checkboxInputClassName="w-4 h-4 bg-gray-100 border-gray-300 rounded focus:ring-2"
                checkboxInputStyle={customCheckboxStyle}
                className="w-[109px] md:w-full lg:w-[120px] px-2 py-2"
                containerClassName="w-max md:w-full lg:w-max"
              />
              <InputDropdown
                options={activities}
                selectedOptions={selectedActivity}
                onOptionsSelect={handleActivitySelect}
                multiSelect={true}
                placeholder="Activity"
                checkboxClassName="mr-2"
                checkboxInputClassName="w-4 h-4 bg-gray-100 border-gray-300 rounded focus:ring-2"
                checkboxInputStyle={customCheckboxStyle}
                className="w-[109px] md:w-full lg:w-[120px] px-2 py-2"
                containerClassName="w-max md:w-full lg:w-max"
              />
              <InputDropdown
                options={assets}
                selectedOptions={selectedAsset}
                onOptionsSelect={handleAssetSelect}
                multiSelect={true}
                placeholder="Assets"
                checkboxClassName="mr-2"
                checkboxInputClassName="w-4 h-4 bg-gray-100 border-gray-300 rounded focus:ring-2"
                checkboxInputStyle={customCheckboxStyle}
                className="w-[109px] md:w-full lg:w-[120px] px-2 py-2"
                containerClassName="w-max md:w-full lg:w-max"
              />
            </div>
          </div>

          <FeaturedDefi featuredData={featuredDefi} />

          {loadingTable && (
            <div className="flex justify-center items-center h-[60vh] ml-0 xl:-ml-[100px]">
              <div className="flex justify-center items-center">
                <Spinner size="lg" />
              </div>
            </div>
          )}

          {selectedActivity.length > 0 &&
            selectedAsset.length > 0 &&
            !loadingTable &&
            Object.keys(filteredSections).map((section) => (
              <Fragment key={section}>
                <div className="overflow-x-auto max-w-full">
                  {getFilteredRows(sections[section]).length > 0 && (
                    <div className="mb-6 flex flex-col ">
                      <DefiListHeader
                        sectionName={section}
                        sortConfig={
                          sortConfigs[section] || {
                            key: "APY",
                            direction: "descending",
                          }
                        }
                        onSort={(key) => handleSort(section, key)}
                      />
                      <DefiListV2
                        rows={getFilteredRows(sections[section])}
                        sortConfig={
                          sortConfigs[section] || {
                            key: "APY",
                            direction: "descending",
                          }
                        }
                        onSort={(key) => handleSort(section, key)}
                      />
                    </div>
                  )}
                </div>
              </Fragment>
            ))}
        </div>
      </div>
      {renderMissingSelectionMessage()}
    </>
  );
};

export default Defi;
